import { Divider } from "@mui/material";
import { useMemo } from "react";

import { Option } from "api/common/types/common.types";
import { ProtectionProfile } from "api/memberPortal/project/protectionProfiles/protectionProfile.types";
import { useGetNiapProtectionProfilesQuery } from "api/memberPortal/project/protectionProfiles/protectionProfiles.api";
import { withForm } from "components/Forms/AppForm";
import SidebarFilter from "components/MUI/DataGrid/Filter/SidebarFilter";

import initialPineFilters from "../ProductsInEvalTable/schemas/initialPineFilter";

import DateTabs from "./components/DateTabs";
import {
  CONFORMANCE_TYPE_OPTIONS,
  KICKOFF_STATUS_OPTIONS,
  STATUS_OPTIONS,
  YES_NO_MAYBE_OPTIONS,
  YES_NO_OPTIONS,
} from "./data/selectOptions";

const cleanPpName = (ppName: string) => {
  // Trying to clean up messy data for sorting and grouping
  // to avoid huge chunks of identical starting phrases and put things in better places
  // There are currently 118 protection profiles that start with the letter "P"
  // 24 start with the letter "c"
  // We also have big hits in "E" (30) for "Extended Package For..." not sure on this one
  // and "U" (61) for "US Government..." Probably dont need to do anything there?
  // "F" (7) has "Functional Package for..." could see a case for this one
  // "V" (1) has "Validated Protection Profile..."
  return ppName
    .replace("Protection Profile for", "") // 70 of them start with this
    .replace("PP-Module for", "") // 45 start with this
    .replace("collaborative Protection Profile for", "") // 19 start with this
    .replace("collaborative Protection Profile Module for", "") // 5 start with this
    .trim(); // Get rid of any spaces left at the start
};

const groupByFirstRelevantLetter = (option: Option) => {
  return cleanPpName(option.label)[0].toUpperCase();
};

const sortPps = (a: Option, b: Option) =>
  cleanPpName(a.label).localeCompare(cleanPpName(b.label));

const EMPTY_ARRAY: ProtectionProfile[] = [];

const PineFilter = withForm({
  defaultValues: initialPineFilters,
  props: { open: true, width: "300px" },
  render: function PineFilter({ form: pineFilterForm, open = true, width }) {
    const { data: niapPps = EMPTY_ARRAY } = useGetNiapProtectionProfilesQuery();
    const conformanceClaims = useMemo(() => {
      if (!niapPps) return [];

      return niapPps
        .map((pp) => ({ label: pp.pp_name, value: pp.pp_id }))
        .sort(sortPps);
    }, [niapPps]);

    const handleClear = () => {
      pineFilterForm.setFieldValue("conformanceType", "any");
    };

    return (
      <SidebarFilter open={open} width={width} anchor="right">
        <pineFilterForm.AppForm>
          {/* Dates */}
          <DateTabs form={pineFilterForm} />
          <Divider />

          {/* Text Search */}
          <pineFilterForm.AppField name="productName">
            {(field) => <field.TextInput label="Product Name" />}
          </pineFilterForm.AppField>

          <pineFilterForm.AppField name="v_id">
            {(field) => <field.TextInput label="VID" />}
          </pineFilterForm.AppField>

          <pineFilterForm.AppField name="vendor">
            {(field) => <field.TextInput label="Vendor" />}
          </pineFilterForm.AppField>

          <pineFilterForm.AppField name="lab">
            {(field) => <field.TextInput label="Lab" />}
          </pineFilterForm.AppField>
          <Divider />

          {/* Radios */}
          <pineFilterForm.AppField name="csfcEligible">
            {(field) => (
              <field.ClearableRadioGroup
                label="CSfC Eligible"
                options={YES_NO_MAYBE_OPTIONS}
              />
            )}
          </pineFilterForm.AppField>

          <pineFilterForm.AppField name="dodCompliant">
            {(field) => (
              <field.ClearableRadioGroup
                options={YES_NO_OPTIONS}
                label="DoD Annex Compliant"
              />
            )}
          </pineFilterForm.AppField>

          <pineFilterForm.AppField name="kickoffStatus">
            {(field) => (
              <field.ClearableRadioGroup
                options={KICKOFF_STATUS_OPTIONS}
                label="Recommended For Kickoff"
              />
            )}
          </pineFilterForm.AppField>

          <pineFilterForm.AppField name="postingRecommended">
            {(field) => (
              <field.ClearableRadioGroup
                options={YES_NO_OPTIONS}
                label="Recommended For Posting"
              />
            )}
          </pineFilterForm.AppField>

          <pineFilterForm.AppField name="acmrApproval">
            {(field) => (
              <field.ClearableRadioGroup
                options={YES_NO_OPTIONS}
                label="ACMR Approval Recommended"
              />
            )}
          </pineFilterForm.AppField>

          <pineFilterForm.AppField name="isResubmission">
            {(field) => (
              <field.ClearableRadioGroup
                options={YES_NO_OPTIONS}
                label="Is Resubmission"
              />
            )}
          </pineFilterForm.AppField>
          <Divider />

          {/* Status */}
          <pineFilterForm.AppField name="status">
            {(field) => (
              <field.MultiCheckbox
                options={STATUS_OPTIONS}
                label="Status"
                maxHeight="180px"
              />
            )}
          </pineFilterForm.AppField>
          <Divider />

          {/* Protection Profiles */}
          <pineFilterForm.AppField name="conformanceType">
            {(field) => (
              <field.ClearableRadioGroup
                options={CONFORMANCE_TYPE_OPTIONS}
                label="Conformance Match"
                disableClear
              />
            )}
          </pineFilterForm.AppField>

          <pineFilterForm.AppField name="ppClaims">
            {(field) => (
              <field.MultiAutocompleteSelect
                options={conformanceClaims}
                label="Conformance"
                groupBy={groupByFirstRelevantLetter}
                getChipLabel={(opt) => {
                  const currentPp = niapPps.find(
                    (pp) => pp.pp_id === opt.value,
                  );

                  return currentPp?.pp_short_name ?? "";
                }}
              />
            )}
          </pineFilterForm.AppField>

          {/* Buttons */}
          <pineFilterForm.FilterButtons onClear={handleClear} />
        </pineFilterForm.AppForm>
      </SidebarFilter>
    );
  },
});

export default PineFilter;
